/**
 * AIInterviewButton Component
 * Dropdown button for creating different types of AI interviews:
 * - AI Interview: Auto-generated questions from job profile
 * - Express Interview: 1 skill, 10 focused questions
 * - Standard Interview: 3 skills, 30 comprehensive questions
 */

import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { MicrophoneIcon, ChevronDownIcon } from '@heroicons/react/24/outline'
import { toast } from 'react-hot-toast'
import { aiInterviewsApi } from '../../services/api'
import InterviewQuestionsPreviewModal from './InterviewQuestionsPreviewModal'
import SkillSelectionModal from './SkillSelectionModal'
import type { AIInterview, AIInterviewQuestion, AIInterviewType, SkillWithExperience } from '../../types/interview'

interface AIInterviewButtonProps {
  resumeId: string
  jobId: string
  matchingPercentage: number
  threshold?: number
  candidateName: string
  jobTitle: string
  onInterviewCreated?: (interview: AIInterview) => void
  onDropdownClose?: () => void // Callback to close parent dropdown
  disabled?: boolean
  className?: string
  showLabel?: boolean // Show text label (for dropdown contexts)
  origin?: 'job' | 'resume' // Where the interview was initiated from
}

const DEFAULT_THRESHOLD = 40

interface InterviewTypeOption {
  type: AIInterviewType
  label: string
  description: string
  skillCount: number | null
  questionCount: string
  color: string
}

// Temporarily disabled — commented out, not deleted, so this can be
// restored by uncommenting and removing the empty array below.
const INTERVIEW_TYPES: InterviewTypeOption[] = [
  {
    type: 'ai_interview',
    label: 'AI Interview',
    description: 'Auto-generated questions from job profile',
    skillCount: null,
    questionCount: 'Variable',
    color: 'blue',
  },
  {
    type: 'express',
    label: 'Express Interview',
    description: 'Select 1 skill - 10 focused questions',
    skillCount: 1,
    questionCount: '10',
    color: 'orange',
  },
  {
    type: 'standard',
    label: 'Standard Interview',
    description: 'Select 2-5 skills - Comprehensive questions',
    skillCount: 3,
    questionCount: '20-50',
    color: 'purple',
  },
]
// const INTERVIEW_TYPES: InterviewTypeOption[] = []

export default function AIInterviewButton({
  resumeId,
  jobId,
  matchingPercentage,
  threshold = DEFAULT_THRESHOLD,
  candidateName,
  jobTitle,
  onInterviewCreated,
  onDropdownClose,
  disabled = false,
  className = '',
  showLabel = false,
  origin = 'resume',
}: AIInterviewButtonProps) {
  const navigate = useNavigate()
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [showPreviewModal, setShowPreviewModal] = useState(false)
  const [showSkillModal, setShowSkillModal] = useState(false)
  const [selectedInterviewType, setSelectedInterviewType] = useState<AIInterviewType | null>(null)
  const [createdInterview, setCreatedInterview] = useState<AIInterview | null>(null)
  const [questions, setQuestions] = useState<AIInterviewQuestion[]>([])
  const [isGeneratingQuestions, setIsGeneratingQuestions] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Matching-score threshold check disabled — interviews allowed for any score.
  // if (matchingPercentage < threshold) {
  //   return null
  // }

  const handleInterviewTypeSelect = (type: AIInterviewType) => {
    setIsDropdownOpen(false)
    setSelectedInterviewType(type)

    if (type === 'ai_interview') {
      // Direct creation for AI Interview
      handleCreateInterview(type)
    } else {
      // Show skill selection modal for Express/Standard
      setShowSkillModal(true)
    }
  }

  const handleSkillsSelected = async (selectedSkills: SkillWithExperience[]) => {
    setShowSkillModal(false)
    if (selectedInterviewType) {
      await handleCreateInterview(selectedInterviewType, selectedSkills)
    }
  }

  const handleCreateInterview = async (
    interviewType: AIInterviewType,
    selectedSkills?: SkillWithExperience[]
  ) => {
    setIsLoading(true)
    try {
      // Create interview - question generation is now async via Celery
      const createResponse = await aiInterviewsApi.create({
        job_id: jobId,
        resume_id: resumeId,
        interview_type: interviewType,
        selected_skills: selectedSkills,
      })

      const interview = createResponse.data as AIInterview & { is_existing?: boolean; is_generating_questions?: boolean }
      setCreatedInterview(interview)

      // Check if this is an existing interview
      if (interview.is_existing) {
        const typeLabel = interviewType === 'ai_interview' ? 'AI Interview' :
                         interviewType === 'express' ? 'Express Interview' : 'Standard Interview'

        // Show appropriate message based on interview status with warning icon
        const toastOptions = { icon: '⚠️' }

        if (interview.status === 'pending') {
          toast(`${typeLabel} already exists. Question generation is in progress.`, toastOptions)
        } else if (interview.status === 'questions_generated') {
          toast(`${typeLabel} already exists with questions ready.`, toastOptions)
          setQuestions(interview.generated_questions as AIInterviewQuestion[] || [])
          setShowPreviewModal(true)
        } else if (interview.status === 'analyzed' || interview.status === 'completed') {
          toast(`${typeLabel} already exists and has been completed.`, toastOptions)
        } else {
          toast(`${typeLabel} already exists for this candidate.`, toastOptions)
        }

        // Close parent dropdown
        if (onDropdownClose) {
          onDropdownClose()
        }

        // Notify parent
        if (onInterviewCreated) {
          onInterviewCreated(interview)
        }

        // Navigate to the Interview tab for existing interview (unless showing preview modal)
        if (interview.status !== 'questions_generated') {
          if (origin === 'job') {
            navigate(`/jobs/${jobId}?tab=interview`)
          } else {
            navigate(`/resumes/${resumeId}?tab=interview`)
          }
        }
        return
      }

      // New interview created - questions are being generated asynchronously
      if (interview.is_generating_questions) {
        const typeLabel = interviewType === 'ai_interview' ? 'AI Interview' :
                         interviewType === 'express' ? 'Express Interview' : 'Standard Interview'
        toast.success(`${typeLabel} created! Question generation is in progress.`)
      }

      // Close parent dropdown
      if (onDropdownClose) {
        onDropdownClose()
      }

      // Notify parent
      if (onInterviewCreated) {
        onInterviewCreated(interview)
      }

      // Navigate to the Interview tab based on origin
      if (origin === 'job') {
        navigate(`/jobs/${jobId}?tab=interview`)
      } else {
        navigate(`/resumes/${resumeId}?tab=interview`)
      }
    } catch (error: any) {
      console.error('Failed to create interview:', error)

      // Extract error message from various possible locations
      let message = 'Failed to create interview'
      if (error?.response?.data?.detail) {
        message = error.response.data.detail
      } else if (error?.response?.data?.message) {
        message = error.response.data.message
      } else if (error?.message) {
        message = error.message
      }

      // Add interview type context to the message
      const typeLabel = selectedInterviewType === 'ai_interview' ? 'AI Interview' :
                       selectedInterviewType === 'express' ? 'Express Interview' : 'Standard Interview'

      toast.error(`${typeLabel}: ${message}`)

      // Close dropdown on error too
      if (onDropdownClose) {
        onDropdownClose()
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleCloseModal = () => {
    setShowPreviewModal(false)
    setCreatedInterview(null)
    setQuestions([])
    setSelectedInterviewType(null)
  }

  const handleCloseSkillModal = () => {
    setShowSkillModal(false)
    setSelectedInterviewType(null)
  }

  // Render as inline list items if showLabel is true (used inside dropdown menus)
  // This shows all 3 interview types as separate items in the parent dropdown
  if (showLabel) {
    return (
      <>
        {/* Interview Type Options - rendered directly in parent dropdown */}
        {INTERVIEW_TYPES.map((option) => (
          <button
            key={option.type}
            onClick={() => handleInterviewTypeSelect(option.type)}
            disabled={disabled || isLoading}
            className={`flex items-center w-full px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150 ${className}`}
          >
            {isLoading && selectedInterviewType === option.type ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600 mr-3 flex-shrink-0"></div>
            ) : (
              <MicrophoneIcon className="h-4 w-4 mr-3 flex-shrink-0 text-green-600" />
            )}
            <div className="text-left flex-1">
              <div className="font-medium">{option.label}</div>
              <div className="text-xs text-gray-500">{option.description}</div>
            </div>
          </button>
        ))}

        {showSkillModal && selectedInterviewType && selectedInterviewType !== 'ai_interview' && (
          <SkillSelectionModal
            isOpen={showSkillModal}
            onClose={handleCloseSkillModal}
            onConfirm={handleSkillsSelected}
            jobId={jobId}
            interviewType={selectedInterviewType as 'express' | 'standard'}
            jobTitle={jobTitle}
          />
        )}

        {showPreviewModal && createdInterview && (
          <InterviewQuestionsPreviewModal
            isOpen={showPreviewModal}
            onClose={handleCloseModal}
            interview={createdInterview}
            questions={questions}
            isGeneratingQuestions={isGeneratingQuestions}
            candidateName={candidateName}
            jobTitle={jobTitle}
            origin={origin}
          />
        )}
      </>
    )
  }

  // Default icon button with dropdown
  return (
    <>
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          disabled={disabled || isLoading}
          className={`text-green-600 hover:text-green-900 p-2 rounded-lg hover:bg-green-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
          title="Create Interview"
        >
          {isLoading ? (
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-green-600"></div>
          ) : (
            <div className="flex items-center">
              <MicrophoneIcon className="h-5 w-5" />
              <ChevronDownIcon className="h-3 w-3 ml-0.5" />
            </div>
          )}
        </button>

        {/* Dropdown Menu */}
        {isDropdownOpen && (
          <div className="absolute right-0 mt-1 w-72 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
            <div className="p-2 border-b border-gray-100">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                Select Interview Type
              </p>
            </div>
            {INTERVIEW_TYPES.map((option) => (
              <button
                key={option.type}
                onClick={() => handleInterviewTypeSelect(option.type)}
                className="w-full px-4 py-3 text-left hover:bg-gray-50 last:rounded-b-lg border-b border-gray-100 last:border-b-0"
              >
                <div className="flex items-center">
                  <MicrophoneIcon className="h-4 w-4 mr-2 flex-shrink-0 text-green-600" />
                  <span className="font-medium text-gray-900">{option.label}</span>
                </div>
                <p className="text-xs text-gray-500 mt-1 ml-6">{option.description}</p>
              </button>
            ))}
          </div>
        )}
      </div>

      {showSkillModal && selectedInterviewType && selectedInterviewType !== 'ai_interview' && (
        <SkillSelectionModal
          isOpen={showSkillModal}
          onClose={handleCloseSkillModal}
          onConfirm={handleSkillsSelected}
          jobId={jobId}
          interviewType={selectedInterviewType as 'express' | 'standard'}
          jobTitle={jobTitle}
        />
      )}

      {showPreviewModal && createdInterview && (
        <InterviewQuestionsPreviewModal
          isOpen={showPreviewModal}
          onClose={handleCloseModal}
          interview={createdInterview}
          questions={questions}
          isGeneratingQuestions={isGeneratingQuestions}
          candidateName={candidateName}
          jobTitle={jobTitle}
          origin={origin}
        />
      )}
    </>
  )
}
